import { useEffect, useRef, useState } from 'react';
import { getConfig } from '../config/environment.js';
import { getMemberRequestToken } from '../api/memberRequestToken.js';

/**
 * Normalize arguments received from SquareGameHub events.
 * Supports both single-object payloads (`{ gameId, squareNumber, heldUntilUtc, memberMaskedName }`)
 * sent by SquareGameNotifier.cs and positional arguments (`(squareNumber, secondArg)`).
 *
 * @param {Array} args - Raw arguments passed by SignalR `.on(...)`
 * @param {string|number|null} [expectedGameId] - Optional active gameId to filter cross-game events
 * @returns {{ squareNumber: number, heldUntilUtc: string|null, memberMaskedName: string|null, gameId: string|null } | null}
 */
export function normalizeSquareEventPayload(args, expectedGameId = null) {
  if (!Array.isArray(args) || args.length === 0) return null;

  let first = args[0];
  if (typeof first === 'string' && first.trim().startsWith('{')) {
    try {
      first = JSON.parse(first);
    } catch {
      // treat as primitive if JSON parse fails
    }
  }

  let rawSquareNumber;
  let heldUntilUtc = null;
  let memberMaskedName = null;
  let payloadGameId = null;

  if (first && typeof first === 'object') {
    payloadGameId = first.gameId ?? first.GameId ?? first.squareGameId ?? first.SquareGameId ?? null;
    if (expectedGameId && payloadGameId && String(payloadGameId) !== String(expectedGameId)) {
      return null;
    }
    rawSquareNumber = first.squareNumber ?? first.SquareNumber ?? first.number ?? first.Number;
    heldUntilUtc = first.heldUntilUtc ?? first.HeldUntilUtc ?? first.heldUntil ?? first.holdExpiry ?? args[1] ?? null;
    memberMaskedName = first.memberMaskedName ?? first.MemberMaskedName ?? first.maskedName ?? args[1] ?? null;
  } else {
    rawSquareNumber = first;
    heldUntilUtc = args[1] ?? null;
    memberMaskedName = args[1] ?? null;
  }

  const squareNumber = typeof rawSquareNumber === 'number' ? rawSquareNumber : Number(rawSquareNumber);
  if (!Number.isFinite(squareNumber) || squareNumber <= 0) {
    return null;
  }

  return {
    squareNumber,
    heldUntilUtc,
    memberMaskedName,
    gameId: payloadGameId != null ? String(payloadGameId) : (expectedGameId != null ? String(expectedGameId) : null),
  };
}

/**
 * useSquareGameConnection — subscribe to real-time Square Game updates for a
 * single game (draw).
 *
 * Builds a HubConnection pointing at `${backendUrl}/hubs/square-game`, joins
 * the `JoinGameGroup` for the supplied `gameId`, and forwards events to the
 * supplied callbacks. Polling consumers can observe `isConnected` and
 * down-shift their polling interval when the hub is live.
 *
 * The hook is intentionally defensive: any error during connect, invoke or
 * event-dispatch is logged and swallowed — consumers should never see it
 * thrown.
 *
 * @param {string|number|null|undefined} gameId - per-game room; when falsy, the hook is a no-op
 * @param {object} [handlers]
 * @param {(squareNumber:number, heldUntilUtc:string) => void} [handlers.onSquareHeld]
 * @param {(squareNumber:number) => void} [handlers.onSquareReleased]
 * @param {(squareNumber:number, memberMaskedName:string) => void} [handlers.onSquarePurchased]
 * @param {(squareNumber:number, memberMaskedName:string) => void} [handlers.onWinnerAnnounced]
 * @returns {{ isConnected: boolean, lastError: Error|null }}
 */
export default function useSquareGameConnection(gameId, handlers = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState(null);
  const connectionRef = useRef(null);
  const hubStateRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryDelayRef = useRef(3000);

  // Keep handlers in a ref so callers can pass inline functions without
  // forcing the hub to rebuild on every render.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!gameId) {
      setIsConnected(false);
      return undefined;
    }

    const config = getConfig();
    if (config.isSocialBot === true) {
      return undefined;
    }

    let connection = null;
    let cancelled = false;
    retryDelayRef.current = 3000;

    const { backendUrl, signalrUrl } = config;
    let hubBase = '';
    if (backendUrl) {
      hubBase = backendUrl.replace(/\/api\/v\d+\/?$/, '').replace(/\/$/, '');
    } else if (signalrUrl) {
      hubBase = signalrUrl.replace(/\/signalr\/?$/, '').replace(/\/$/, '');
    }
    if (!hubBase) {
      console.warn('[SquareGameHub] No backendUrl/signalrUrl in config; skipping');
      return undefined;
    }
    const hubUrl = `${hubBase}/hubs/square-game`;
    const gameIdStr = String(gameId);

    const joinGroupWithRetry = async (conn, retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        if (cancelled) return false;
        try {
          await conn.invoke('JoinGameGroup', gameIdStr);
          return true;
        } catch (err) {
          console.warn(`[SquareGameHub] JoinGameGroup attempt ${attempt}/${retries} failed`, err);
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
      return false;
    };

    import('@microsoft/signalr').then(({ HubConnectionBuilder, HubConnectionState, LogLevel }) => {
      if (cancelled) return;

      try {
        connection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => getMemberRequestToken(),
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
          .configureLogging(LogLevel.Warning)
          .build();
      } catch (err) {
        console.warn('[SquareGameHub] Failed to build connection', err);
        setLastError(err);
        return;
      }

      connectionRef.current = connection;
      hubStateRef.current = HubConnectionState;

      const safeCall = (fn, ...args) => {
        if (typeof fn !== 'function') return;
        try {
          fn(...args);
        } catch (err) {
          console.warn('[SquareGameHub] handler error', err);
        }
      };

      connection.on('SquareHeld', (...args) => {
        const parsed = normalizeSquareEventPayload(args, gameIdStr);
        if (!parsed) return;
        safeCall(handlersRef.current.onSquareHeld, parsed.squareNumber, parsed.heldUntilUtc);
      });
      connection.on('SquareReleased', (...args) => {
        const parsed = normalizeSquareEventPayload(args, gameIdStr);
        if (!parsed) return;
        safeCall(handlersRef.current.onSquareReleased, parsed.squareNumber);
      });
      connection.on('SquarePurchased', (...args) => {
        const parsed = normalizeSquareEventPayload(args, gameIdStr);
        if (!parsed) return;
        safeCall(handlersRef.current.onSquarePurchased, parsed.squareNumber, parsed.memberMaskedName);
      });
      connection.on('WinnerAnnounced', (...args) => {
        const parsed = normalizeSquareEventPayload(args, gameIdStr);
        if (!parsed) return;
        safeCall(handlersRef.current.onWinnerAnnounced, parsed.squareNumber, parsed.memberMaskedName);
      });

      const scheduleRetry = () => {
        if (cancelled) return;
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, 30000);
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          start();
        }, delay);
      };

      connection.onreconnecting(() => {
        if (cancelled) return;
        setIsConnected(false);
      });

      connection.onreconnected(async () => {
        if (cancelled) return;
        const joined = await joinGroupWithRetry(connection);
        if (cancelled) return;
        if (joined) {
          retryDelayRef.current = 3000;
          setIsConnected(true);
          setLastError(null);
        } else {
          setIsConnected(false);
          connection.stop().catch(() => {});
          scheduleRetry();
        }
      });

      connection.onclose((err) => {
        if (cancelled) return;
        setIsConnected(false);
        if (err) setLastError(err);
        scheduleRetry();
      });

      const start = async () => {
        if (cancelled || !connection) return;
        if (connection.state !== HubConnectionState.Disconnected) return;

        try {
          await connection.start();
          if (cancelled) {
            connection.stop().catch(() => {});
            return;
          }
          const joined = await joinGroupWithRetry(connection);
          if (cancelled) {
            connection.stop().catch(() => {});
            return;
          }
          if (joined) {
            retryDelayRef.current = 3000;
            setIsConnected(true);
            setLastError(null);
          } else {
            setIsConnected(false);
            connection.stop().catch(() => {});
            scheduleRetry();
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('[SquareGameHub] start failed; falling back to polling', err);
            setIsConnected(false);
            setLastError(err);
            scheduleRetry();
          }
        }
      };

      const tryImmediateReconnect = () => {
        if (cancelled || !connection) return;
        if (connection.state === HubConnectionState.Disconnected) {
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          retryDelayRef.current = 2000;
          start();
        }
      };

      window.addEventListener('online', tryImmediateReconnect);
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          tryImmediateReconnect();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      connection._customListenersCleanup = () => {
        window.removeEventListener('online', tryImmediateReconnect);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };

      start();
    }).catch((err) => {
      if (!cancelled) {
        console.warn('[SquareGameHub] Failed to load SignalR library', err);
        setLastError(err);
      }
    });

    return () => {
      cancelled = true;
      setIsConnected(false);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      const conn = connectionRef.current;
      if (!conn) return;
      connectionRef.current = null;
      conn._customListenersCleanup?.();
      const HubConnectionState = hubStateRef.current;
      const teardown = async () => {
        try {
          if (HubConnectionState && conn.state === HubConnectionState.Connected) {
            try {
              await conn.invoke('LeaveGameGroup', gameIdStr);
            } catch {
              /* best-effort */
            }
          }
          await conn.stop();
        } catch (err) {
          console.debug('[SquareGameHub] stop error', err);
        }
      };
      teardown();
    };
  }, [gameId]);

  return { isConnected, lastError };
}
